/**
 * The signed-in shell, one for every role (decision: the BO-* boards draw a 64 px top bar, every other back-office
 * board — Admin-*, AF-*, FS-*, TD-*, AG-*, AC-* — the dark sidebar; the sidebar wins so that navigation is one model
 * whatever the role, and the top bar's pieces live in it: switcher → content row, search / Finance / Paramètres /
 * account menu → sidebar).
 *
 *  ≥ 1024 px: sticky 248 px sidebar (collapsible to 72 px, remembered), content padding 32/36/60 at ≥ 1280.
 *  < 1024 px: 56 px dark top bar with the menu button; the sidebar opens as a 264 px drawer over a 30 % veil.
 *  Content width: capped at 1480 px and centred, so 1920+ screens do not stretch tables absurdly.
 */
import { Suspense, useEffect, useState } from "react";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Menu } from "lucide-react";
import { call, IS_DEMO, SESSION_EXPIRED_EVENT } from "@/api/client";
import { LogoSymbol } from "@/brand/logo";
import { staffAuthApi } from "@/contracts/staff-auth";
import { useSession, useSetSession } from "@/auth/session";
import { cn } from "@/lib/cn";
import { OfflineStrip } from "@/ui/offline-strip";
import { PageSkeleton } from "@/ui/page";
import { useToast } from "@/ui/toast";
import { useShellPosition } from "./active";
import { SPACE_LABEL } from "./nav";
import { Sidebar } from "./sidebar";
import { SpaceSwitcher } from "./space-switcher";

const COLLAPSE_KEY = "melrys.admin.sidebar.collapsed";
const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
};

export function DemoBanner() {
  if (!IS_DEMO) return null;
  return (
    <div role="status" className="sticky top-0 z-50 flex min-h-[var(--demo-h)] items-center justify-center bg-lime px-4 py-1 text-center text-[12px] font-bold leading-tight text-ink">
      Démo — données fictives, aucune action n'est réellement exécutée.
    </div>
  );
}

export function Shell() {
  const session = useSession();
  const setSession = useSetSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { space, spaces, navKey } = useShellPosition(session.user.role);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawer, setDrawer] = useState(false);

  // A 401 anywhere: back to the sign-in page with the amber banner, then back here.
  useEffect(() => {
    const onExpired = () => {
      setSession(null);
      void navigate({ to: "/connexion", search: { expire: "1", retour: window.location.pathname + window.location.search } });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [navigate, setSession]);

  const toggle = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1");
      } catch {
        /* private mode */
      }
      return !c;
    });
  };

  const logout = async () => {
    try {
      await call(staffAuthApi.logout);
    } catch {
      /* signed out locally anyway */
    }
    qc.clear();
    setSession(null);
    toast("Vous êtes déconnecté.");
    void navigate({ to: "/connexion" });
  };

  const common = { session, space, navKey, onLogout: logout };

  return (
    // --demo-h: height of the sticky « Démo » strip (0 outside demo builds); sticky bars and the sidebar sit under it.
    <div className={cn("min-h-dvh bg-canvas", IS_DEMO ? "[--demo-h:30px]" : "[--demo-h:0px]")}>
      <DemoBanner />
      {/* < 1024 px: top bar + drawer */}
      <header className="sticky top-[var(--demo-h)] z-30 flex h-14 items-center gap-3 bg-shell px-4 text-white lg:hidden">
        <button type="button" onClick={() => setDrawer(true)} aria-label="Ouvrir le menu" aria-expanded={drawer} className="inline-flex size-9 items-center justify-center rounded-[9px] bg-white/[.07] text-shell-text hover:text-white">
          <Menu className="size-[18px]" />
        </button>
        <LogoSymbol height={22} />
        <span className="text-[14.5px] font-bold tracking-[-0.02em]">Melrys</span>
        <span className="ml-auto rounded-lg bg-white/[.08] px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.06em]">{SPACE_LABEL[space]}</span>
      </header>
      {drawer && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <button type="button" aria-label="Fermer le menu" className="absolute inset-0 bg-black/30" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 max-w-[calc(100vw-40px)] shadow-flyout">
            <Sidebar {...common} collapsed={false} onClose={() => setDrawer(false)} />
          </div>
        </div>
      )}
      <div className="flex">
        <aside className={cn("sticky top-[var(--demo-h)] hidden h-[calc(100dvh-var(--demo-h))] shrink-0 lg:block", collapsed ? "w-[72px]" : "w-[248px]")}>
          <Sidebar {...common} collapsed={collapsed} onToggle={toggle} />
        </aside>
        <main className="min-w-0 flex-1 px-4 pb-[60px] pt-5 sm:px-6 sm:pt-6 xl:px-9 xl:pt-8">
          <div className="mx-auto w-full max-w-[1480px]">
            <OfflineStrip />
            <SpaceSwitcher spaces={spaces} current={space} role={session.user.role} />
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
